import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Calculator,
  Plus,
  Edit,
  Trash2,
  RefreshCw,
  Check,
  X,
  Power,
  PowerOff,
} from 'lucide-react';
import { useBusiness } from '@/contexts/BusinessContext';
import { usePageTour, WelcomeModal } from '@/walkthrough';
import { commissionRulesPageTour } from '@/walkthrough/tourSteps';
import {
  useCommissionRulesByBusiness,
  useCreateCommissionRule,
  useUpdateCommissionRule,
  useDeleteCommissionRule,
  useToggleRuleActive,
} from '@/hooks/useCommissionRules';
import { useAppConfig } from '@/hooks/useAppConfig';
import { formatRuleDescription, recalculateCommissions } from '@/services/commissionRules';
import { PROJECT_TYPE_OPTIONS } from '@/config/projectTypes';
import type { CommissionRule, CreateCommissionRuleInput, Business, CommissionType } from '@/types';

// ===========================================
// Types
// ===========================================

interface RuleFormData {
  projectType: string;
  commissionType: CommissionType;
  commissionValue: number;
  commissionCap: number | null;
  description: string;
}

// ===========================================
// Main Component
// ===========================================

export function CommissionRulesPage() {
  const { currentBusiness, getBusinessLabel, businesses } = useBusiness();
  const { showWelcomeModal, startTour, skipTour, globalOptOut } = usePageTour(commissionRulesPageTour);
  const [selectedBusiness, setSelectedBusiness] = useState<Business>(currentBusiness);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingRule, setEditingRule] = useState<CommissionRule | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const { data: rules, isLoading } = useCommissionRulesByBusiness(selectedBusiness);
  const deleteRuleMutation = useDeleteCommissionRule();
  const toggleActiveMutation = useToggleRuleActive();
  const [recalculating, setRecalculating] = useState(false);

  const handleRecalculate = async () => {
    setRecalculating(true);
    try {
      const res = await recalculateCommissions(selectedBusiness);
      toast.success(
        `Recalculated commissions: ${res.updated} updated, ${res.unchanged} unchanged` +
          (res.errors ? `, ${res.errors} errors` : '')
      );
    } catch (error) {
      console.error('Recalculate error:', error);
      toast.error('Failed to recalculate commissions');
    } finally {
      setRecalculating(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteRuleMutation.mutateAsync(id);
      setDeleteConfirmId(null);
    } catch {
      // Error handled by mutation
    }
  };

  const handleToggleActive = async (rule: CommissionRule) => {
    await toggleActiveMutation.mutateAsync({
      id: rule.id,
      isActive: !rule.isActive,
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">Commission Rules</h1>
          <p className="page-description">
            Configure commission calculations by business and project type.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleRecalculate}
            disabled={recalculating}
            className="btn btn-secondary"
            title="Recompute commissions on already-synced projects using the current rules"
          >
            <RefreshCw size={20} className={recalculating ? 'animate-spin' : ''} />
            {recalculating ? 'Recalculating…' : 'Recalculate'}
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            data-tour="add-rule"
            className="btn btn-primary"
          >
            <Plus size={20} />
            Add Rule
          </button>
        </div>
      </div>

      {/* Business filter */}
      <div data-tour="business-toggle" className="card">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <label className="font-medium text-gray-700">Filter by Business:</label>
          <div className="flex gap-2">
            {businesses.map((business) => (
              <button
                key={business}
                onClick={() => setSelectedBusiness(business)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  selectedBusiness === business
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {getBusinessLabel(business)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Info box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-medium text-blue-900">How Commission Rules Work</h3>
        <ul className="mt-2 text-sm text-blue-800 space-y-1 list-disc list-inside">
          <li>Each rule matches a specific business + project type combination</li>
          <li>Commission is calculated as: <code className="bg-blue-100 px-1 rounded">totalRevenue × percentage</code> or a flat amount</li>
          <li>Total revenue = Contract Amount + Change Order Total</li>
          <li>Optional cap limits the maximum commission per project</li>
        </ul>
      </div>

      {/* Rules table */}
      {isLoading ? (
        <div className="card">
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="animate-spin text-blue-600" size={32} />
          </div>
        </div>
      ) : rules && rules.length > 0 ? (
        <div data-tour="rules-table" className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Project Type</th>
                <th>Commission</th>
                <th>Description</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rules.map((rule) => (
                <tr key={rule.id} className={!rule.isActive ? 'opacity-50' : ''}>
                  <td className="font-medium text-gray-900">{rule.projectType}</td>
                  <td>
                    <span className="font-mono text-sm">
                      {formatRuleDescription(rule)}
                    </span>
                  </td>
                  <td className="text-sm text-gray-600">
                    {rule.description || '—'}
                  </td>
                  <td>
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        rule.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {rule.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="flex gap-1">
                      <button
                        onClick={() => setEditingRule(rule)}
                        className="p-2 text-gray-500 hover:text-blue-600 hover:bg-gray-100 rounded"
                        title="Edit rule"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => handleToggleActive(rule)}
                        className={`p-2 hover:bg-gray-100 rounded ${
                          rule.isActive
                            ? 'text-gray-500 hover:text-orange-600'
                            : 'text-gray-500 hover:text-green-600'
                        }`}
                        title={rule.isActive ? 'Deactivate' : 'Activate'}
                      >
                        {rule.isActive ? <PowerOff size={18} /> : <Power size={18} />}
                      </button>
                      <button
                        onClick={() => setDeleteConfirmId(rule.id)}
                        className="p-2 text-gray-500 hover:text-red-600 hover:bg-gray-100 rounded"
                        title="Delete rule"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="card">
          <div className="empty-state">
            <Calculator size={48} className="empty-state-icon" />
            <p className="text-lg font-medium text-gray-900">
              No rules for {getBusinessLabel(selectedBusiness)}
            </p>
            <p className="mt-1 mb-4">
              Add commission rules to calculate rep commissions automatically.
            </p>
            <button
              onClick={() => setShowAddModal(true)}
              className="btn btn-primary"
            >
              <Plus size={20} />
              Add First Rule
            </button>
          </div>
        </div>
      )}

      {/* Example rules */}
      <div className="card">
        <h3 className="font-medium text-gray-900 mb-3">Example Rules</h3>
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Business</th>
                <th>Project Type</th>
                <th>Type</th>
                <th>Value</th>
                <th>Cap</th>
              </tr>
            </thead>
            <tbody className="text-gray-600">
              <tr>
                <td>ACR</td>
                <td>Water Damage</td>
                <td>Percentage</td>
                <td>5%</td>
                <td>$2,000</td>
              </tr>
              <tr>
                <td>ACR</td>
                <td>Fire Damage</td>
                <td>Percentage</td>
                <td>7%</td>
                <td>No cap</td>
              </tr>
              <tr>
                <td>Blacksmith</td>
                <td>Reconstruction</td>
                <td>Flat</td>
                <td>$500</td>
                <td>N/A</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-sm text-gray-500">
          These are examples only. Configure your actual rules above.
        </p>
      </div>

      {/* Add/Edit Rule Modal */}
      {(showAddModal || editingRule) && (
        <RuleFormModal
          business={selectedBusiness}
          rule={editingRule}
          onClose={() => {
            setShowAddModal(false);
            setEditingRule(null);
          }}
        />
      )}

      <WelcomeModal
        pageName={commissionRulesPageTour.pageName}
        onStartTour={startTour}
        onSkip={skipTour}
        onGlobalOptOut={globalOptOut}
        open={showWelcomeModal}
      />

      {/* Delete confirmation modal */}
      {deleteConfirmId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900">Delete Rule</h3>
            <p className="mt-2 text-gray-600">
              Are you sure you want to delete this commission rule? This action cannot be undone.
            </p>
            <div className="mt-6 flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirmId(null)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirmId)}
                disabled={deleteRuleMutation.isPending}
                className="btn btn-danger"
              >
                {deleteRuleMutation.isPending ? (
                  <>
                    <RefreshCw className="animate-spin" size={18} />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={18} />
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ===========================================
// Rule Form Modal Component
// ===========================================

interface RuleFormModalProps {
  business: Business;
  rule: CommissionRule | null;
  onClose: () => void;
}

function RuleFormModal({ business, rule, onClose }: RuleFormModalProps) {
  const createRuleMutation = useCreateCommissionRule();
  const updateRuleMutation = useUpdateCommissionRule();
  const { getBusinessLabel } = useBusiness();
  const { data: config } = useAppConfig();
  const [showCustomType, setShowCustomType] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<RuleFormData>({
    defaultValues: rule
      ? {
          projectType: rule.projectType,
          commissionType: rule.commissionType,
          commissionValue:
            rule.commissionType === 'percentage'
              ? rule.commissionValue * 100
              : rule.commissionValue,
          commissionCap: rule.commissionCap || null,
          description: rule.description || '',
        }
      : {
          projectType: '',
          commissionType: 'percentage',
          commissionValue: 5,
          commissionCap: null,
          description: '',
        },
  });

  const commissionType = watch('commissionType');
  const projectType = watch('projectType');
  // Prefer any admin-synced options, but fall back to the canonical Portal
  // project types so rules always match what the Portal actually stores.
  const configuredOptions = (config?.projectTypeOptions as string[] | undefined) || [];
  const projectTypeOptions = configuredOptions.length > 0 ? configuredOptions : PROJECT_TYPE_OPTIONS;

  // Check if current project type is custom (not in options list)
  const isCustomType = rule && projectType && projectTypeOptions.length > 0 && !projectTypeOptions.includes(projectType);

  // Show custom input if editing a custom type
  React.useEffect(() => {
    if (isCustomType) {
      setShowCustomType(true);
    }
  }, [isCustomType]);

  const onSubmit = async (data: RuleFormData) => {
    const input: CreateCommissionRuleInput = {
      business,
      projectType: data.projectType,
      commissionType: data.commissionType,
      commissionValue:
        data.commissionType === 'percentage'
          ? data.commissionValue / 100
          : data.commissionValue,
      commissionCap: data.commissionCap || undefined,
      description: data.description || undefined,
    };

    try {
      if (rule) {
        await updateRuleMutation.mutateAsync({
          id: rule.id,
          updates: input,
        });
      } else {
        await createRuleMutation.mutateAsync(input);
      }
      onClose();
    } catch {
      // Error handled by mutation
    }
  };

  const isLoading = createRuleMutation.isPending || updateRuleMutation.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h3 className="text-lg font-semibold text-gray-900">
          {rule ? 'Edit Commission Rule' : 'Add Commission Rule'}
        </h3>
        <p className="mt-1 text-sm text-gray-600">
          For {getBusinessLabel(business)} projects
        </p>

        <form onSubmit={handleSubmit(onSubmit)} className="mt-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Project Type *
            </label>
            {projectTypeOptions.length > 0 && !showCustomType ? (
              <>
                <select
                  {...register('projectType', { required: 'Project type is required' })}
                  className="input"
                  onChange={(e) => {
                    if (e.target.value === '__custom__') {
                      setShowCustomType(true);
                      setValue('projectType', '');
                    }
                  }}
                >
                  <option value="">Select a project type...</option>
                  {projectTypeOptions.map((type: string) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                  <option value="__custom__">Other (custom type)</option>
                </select>
                {errors.projectType && (
                  <p className="mt-1 text-sm text-red-600">{errors.projectType.message}</p>
                )}
              </>
            ) : (
              <>
                <input
                  {...register('projectType', { required: 'Project type is required' })}
                  type="text"
                  className="input"
                  placeholder="Must match the Portal type exactly, e.g. Water, Fire, Mold"
                />
                {errors.projectType && (
                  <p className="mt-1 text-sm text-red-600">{errors.projectType.message}</p>
                )}
                {projectTypeOptions.length > 0 && showCustomType && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowCustomType(false);
                      setValue('projectType', '');
                    }}
                    className="mt-1 text-sm text-blue-600 hover:text-blue-800"
                  >
                    ← Back to dropdown
                  </button>
                )}
                {projectTypeOptions.length === 0 && (
                  <p className="mt-1 text-xs text-gray-500">
                    Project types will be automatically detected from synced projects
                  </p>
                )}
              </>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Commission Type *
            </label>
            <select {...register('commissionType')} className="input">
              <option value="percentage">Percentage of Revenue</option>
              <option value="flat">Flat Amount</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {commissionType === 'percentage' ? 'Percentage (%)' : 'Amount ($)'} *
            </label>
            <input
              {...register('commissionValue', {
                required: 'Value is required',
                min: { value: 0, message: 'Must be positive' },
                max:
                  commissionType === 'percentage'
                    ? { value: 100, message: 'Max 100%' }
                    : undefined,
              })}
              type="number"
              step={commissionType === 'percentage' ? '0.1' : '1'}
              className="input"
              placeholder={commissionType === 'percentage' ? '5' : '500'}
            />
            {errors.commissionValue && (
              <p className="mt-1 text-sm text-red-600">{errors.commissionValue.message}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              {commissionType === 'percentage'
                ? 'Enter as percentage (e.g., 5 for 5%)'
                : 'Enter dollar amount'}
            </p>
          </div>

          {commissionType === 'percentage' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Commission Cap ($)
              </label>
              <input
                {...register('commissionCap', {
                  min: { value: 0, message: 'Must be positive' },
                })}
                type="number"
                step="1"
                className="input"
                placeholder="Leave blank for no cap"
              />
              {errors.commissionCap && (
                <p className="mt-1 text-sm text-red-600">{errors.commissionCap.message}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Optional maximum commission per project
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <input
              {...register('description')}
              type="text"
              className="input"
              placeholder="Optional notes about this rule"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="btn btn-secondary flex-1">
              Cancel
            </button>
            <button type="submit" disabled={isLoading} className="btn btn-primary flex-1">
              {isLoading ? (
                <>
                  <RefreshCw className="animate-spin" size={18} />
                  Saving...
                </>
              ) : (
                <>
                  <Check size={18} />
                  {rule ? 'Update Rule' : 'Create Rule'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
